/**
 * @description Returns true if `userAgent` includes the one used for the mobile app, false otherwise.
 * @param {string} userAgent
 * @returns {boolean}
 */
const isMobileApp = (userAgent) => userAgent.includes('@tictactrip/app');

/**
 * Oldest app version the site still serves. Below it, the WebView refuses the
 * Cloudflare Turnstile iframe, so the app cannot mint the session the API
 * requires: nothing works, and the visitor is asked to update instead of
 * hitting errors. Raise it only when a new version becomes mandatory.
 */
const MINIMUM_SUPPORTED_APP_VERSION = [1, 8, 1];

/**
 * 1.7.0 and older replace the whole user agent, newer versions append their
 * token to the WebView one:
 *   '@tictactrip/app v1.7.0 (Android; 14; 24117RN76E)'
 *   'Mozilla/5.0 (iPhone; …) Mobile/15E148 @tictactrip/app/1.8.1'
 */
const APP_VERSION_REGEX = /@tictactrip\/app[ /]v?(\d+)\.(\d+)\.(\d+)/;

/**
 * @description the app version behind a user agent, as [major, minor, patch]
 * @param {string} userAgent
 * @returns {number[] | null} null when the user agent carries no readable version
 */
const getAppVersion = (userAgent) => {
  const match = userAgent?.match(APP_VERSION_REGEX);

  return match ? match.slice(1, 4).map(Number) : null;
};

/**
 * @description whether the app running this page is too old to be served.
 * A user agent we cannot read a version from is left alone: showing the update
 * wall to someone who cannot be helped by it is worse than serving the site.
 * @param {string} userAgent
 * @returns {boolean}
 */
const isOutdatedApp = (userAgent) => {
  const version = getAppVersion(userAgent ?? '');

  if (!version) {
    return false;
  }

  // The first differing number decides: 1.10.0 is newer than 1.9.9, and
  // 2.0.0 is newer than 1.8.1.
  for (let index = 0; index < MINIMUM_SUPPORTED_APP_VERSION.length; index++) {
    if (version[index] !== MINIMUM_SUPPORTED_APP_VERSION[index]) {
      return version[index] < MINIMUM_SUPPORTED_APP_VERSION[index];
    }
  }

  return false;
};

export { getAppVersion, isMobileApp, isOutdatedApp };
